import { CopilotDocument } from '../_interfaces/document';
import {
  AbbreviationPolicy,
  ClinicianReference,
  DetailLevel,
  EmptySectionPolicy,
  FormatMode,
  NoteGenerationSettings,
  PatientReference,
  QuotationPolicy,
} from '../_interfaces/note-generation.interface';

export const DEFAULT_NOTE_GENERATION_SETTINGS: NoteGenerationSettings = {
  detailLevel: DetailLevel.Balanced,
  formatMode: FormatMode.Hybrid,
  abbreviationPolicy: AbbreviationPolicy.Balanced,
  quotationPolicy: QuotationPolicy.SalientOnly,
  emptySectionPolicy: EmptySectionPolicy.LeaveBlank,
  referClinicianAs: ClinicianReference.FirstPerson,
  referPatientAs: PatientReference.NameIfAvailableElseThey,
};

const DOCUMENT_STYLE_KEYS = [
  'detailLevel',
  'formatMode',
  'abbreviationPolicy',
  'quotationPolicy',
] as const;

export interface GenerationRequestFields {
  config?: NoteGenerationSettings;
}

export type DocumentGenerationFields = Pick<CopilotDocument, 'generationSettings' | 'hasCustomSettings'>;

export function hasNoteGenerationSettings(
  value: unknown,
): value is NoteGenerationSettings {
  return !!value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length > 0;
}

export function completeNoteGenerationSettings(
  partial?: Partial<NoteGenerationSettings> | null,
): NoteGenerationSettings {
  if (!hasNoteGenerationSettings(partial)) {
    return { ...DEFAULT_NOTE_GENERATION_SETTINGS };
  }
  return { ...DEFAULT_NOTE_GENERATION_SETTINGS, ...partial };
}

// Temporary: the API still returns minimal / balanced / liberal. Until that is reduced to Off/On,
// map minimal → Off and both balanced and liberal → On.
export function toAbbreviationToggle(policy: AbbreviationPolicy): AbbreviationPolicy {
  return policy === AbbreviationPolicy.Minimal
    ? AbbreviationPolicy.Minimal
    : AbbreviationPolicy.Balanced;
}

export function withAbbreviationToggle(settings: NoteGenerationSettings): NoteGenerationSettings {
  return {
    ...settings,
    abbreviationPolicy: toAbbreviationToggle(settings.abbreviationPolicy),
  };
}

export function documentHasCustomSettings(
  doc?: Pick<CopilotDocument, 'hasCustomSettings'> | null,
): boolean {
  return doc?.hasCustomSettings === true;
}

export function resolveGenerationConfig(input: {
  previousDoc?: DocumentGenerationFields | null;
  config?: NoteGenerationSettings;
}): NoteGenerationSettings | undefined {
  if (hasNoteGenerationSettings(input.config)) {
    return input.config;
  }
  if (
    documentHasCustomSettings(input.previousDoc)
    && hasNoteGenerationSettings(input.previousDoc?.generationSettings)
  ) {
    return input.previousDoc.generationSettings;
  }
  return undefined;
}

export function buildGenerationRequestFields(input: {
  previousDoc?: DocumentGenerationFields | null;
  config?: NoteGenerationSettings;
}): GenerationRequestFields {
  const config = resolveGenerationConfig(input);
  return hasNoteGenerationSettings(config) ? { config } : {};
}

export function resolveDocumentStyleSettings(input: {
  userSettings?: Partial<NoteGenerationSettings> | null;
  documentSettings?: Partial<NoteGenerationSettings> | null;
  hasCustomSettings?: boolean;
}): NoteGenerationSettings {
  if (input.hasCustomSettings) {
    return completeNoteGenerationSettings(input.documentSettings);
  }
  return completeNoteGenerationSettings(input.userSettings);
}

export function overrideGenerationSettingsForApply(
  seed: NoteGenerationSettings,
  current: NoteGenerationSettings,
): NoteGenerationSettings | undefined {
  const hasChanges = DOCUMENT_STYLE_KEYS.some(key => current[key] !== seed[key]);
  if (!hasChanges) {
    return undefined;
  }
  return completeNoteGenerationSettings({ ...seed, ...pickDocumentStyle(current) });
}

export function storedGenerationFieldsFromResponse(
  response?: { settings?: Partial<NoteGenerationSettings> | null } | null,
  previous?: DocumentGenerationFields | null,
  hasCustomSettings = false,
): DocumentGenerationFields {
  const settings = hasNoteGenerationSettings(response?.settings)
    ? completeNoteGenerationSettings(response?.settings)
    : (hasNoteGenerationSettings(previous?.generationSettings) ? previous?.generationSettings : undefined);

  const fields: DocumentGenerationFields = { hasCustomSettings };
  if (settings) {
    fields.generationSettings = settings;
  }
  return fields;
}

function pickDocumentStyle(settings: NoteGenerationSettings): Pick<
  NoteGenerationSettings,
  (typeof DOCUMENT_STYLE_KEYS)[number]
> {
  return {
    detailLevel: settings.detailLevel,
    formatMode: settings.formatMode,
    abbreviationPolicy: settings.abbreviationPolicy,
    quotationPolicy: settings.quotationPolicy,
  };
}
